import React, { useEffect, useState, useMemo, useCallback, FC } from 'react';
import { useIntl } from 'react-intl';

import {
  CardSelection,
  PaymentMethod,
  PaymentMethodFirstTimeFreeCoupon,
  SettingPayment,
} from '../../../../../../../interfaces/merchants/resources/books';
import { UserMeReduxState } from '../../../../../../../interfaces/users';
import { CreditCard } from '../../../../../../../interfaces/creditCard';

import { scrollToTop } from '../../../../../../../hooks/useScrollToTop';
import { State as ResourcesBooksInputsState } from '../../../../../../../interfaces/merchants/resources/books/inputs';

import InputPaymentCreditCard, {
  submittable as creditCardSubmittable,
} from '../InputPaymentCreditCard';
import css from './InputPayment.module.scss';
import { PaymentMethodSelect } from '../../../PaymentMethodSelect';
import { PaymentMethodsWithCoupon } from '../PaymentMethodsWithCoupon';
import classNames from 'classnames';
import { PaymentMethodErrorMessage } from './PaymentMethodErrorMessage';
import { RSButton } from 'src/reservation-page/RSButton';

export type PaymentMethodError = string;

interface ElementProps {
  settings: SettingPayment;
  postable: boolean;
  loginUser: UserMeReduxState;
  userEmail: string;
  onSubmit: (params: {
    cardSelection?: CardSelection;
    subscriptionSelection?: string;
    ticketSelection?: string;
    creditCard?: CreditCard;
    firstTimeFreeCouponPublicId?: string;
    paymentMethod: PaymentMethod;
    email: string;
  }) => void;
  inputs: ResourcesBooksInputsState;
  isAvailabelFirstTimeFreeCoupon: boolean;
  firstTimeFreeCoupon?: {
    publicId: string;
    name: string;
    paymentMethod?: PaymentMethodFirstTimeFreeCoupon;
  };
  error?: PaymentMethodError;
}

interface State {
  paymentMethod?: PaymentMethod;
  creditCard?: CreditCard;
  cardSelection?: CardSelection;
  subscriptionSelection?: string;
  ticketSelection?: string;
}

type Props = ElementProps;

const showableInputMyCreditCard = (
  me: UserMeReduxState,
  paymentMethod?: PaymentMethod,
): boolean => {
  if (!paymentMethod) return false;
  if (paymentMethod.method !== 'credit_card') return false;
  if (!me.creditCard) {
    return false;
  }
  return true;
};

const showableInputMySubscription = (
  paymentMethod?: PaymentMethod,
): boolean => {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod.method !== 'subscription') {
    return false;
  }
  return true;
};

const showableInputMyTicket = (paymentMethod?: PaymentMethod): boolean => {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod.method !== 'ticket') {
    return false;
  }
  return true;
};

const showableInputCreditCard = (
  me: UserMeReduxState,
  paymentMethod?: PaymentMethod,
  cardSelection?: CardSelection,
): boolean => {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod.method !== 'credit_card') {
    return false;
  }
  if (!me.creditCard) {
    return true;
  }
  if (!cardSelection) {
    return false;
  }
  return cardSelection === CardSelection.Another;
};

const createSubmittable = (me: UserMeReduxState, state: State): boolean => {
  if (!state.paymentMethod) {
    return false;
  }
  if (
    state.paymentMethod.method === 'onsite' ||
    state.paymentMethod.method === 'bank_transfer'
  ) {
    return true;
  }
  if (state.paymentMethod.method === 'subscription') {
    return !!state.subscriptionSelection;
  }
  if (state.paymentMethod.method === 'first_time_free_voucher') {
    return true;
  }
  if (state.paymentMethod.method === 'ticket') {
    return !!state.ticketSelection;
  }
  if (!me.creditCard) {
    return !!state.creditCard && creditCardSubmittable(state.creditCard);
  }
  if (!state.cardSelection) {
    return false;
  }
  if (state.cardSelection === CardSelection.Used) {
    return true;
  }
  return !!state.creditCard && creditCardSubmittable(state.creditCard);
};

const InputPayment: FC<Props> = ({
  inputs,
  loginUser,
  settings: { paymentMethods, errors },
  postable,
  onSubmit,
  isAvailabelFirstTimeFreeCoupon,
  firstTimeFreeCoupon,
  userEmail,
  error,
}) => {
  const intl = useIntl();
  useEffect(() => {
    scrollToTop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // paymentMethodが選択されている支払い方法
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(
    undefined,
  );

  // useEffectの中でsetStateしたくないが、いい方法が思いつかなかった...
  useEffect(() => {
    // 初回無料の場合はdefaultで初回無料クーポンが選択されている
    if (firstTimeFreeCoupon?.paymentMethod && isAvailabelFirstTimeFreeCoupon) {
      setPaymentMethod(firstTimeFreeCoupon.paymentMethod);
    } else if (inputs.payment) {
      setPaymentMethod(inputs.payment.paymentMethod);
    }
  }, [
    inputs.payment,
    firstTimeFreeCoupon?.paymentMethod,
    isAvailabelFirstTimeFreeCoupon,
  ]);

  const [creditCard, setCreditCard] = useState<CreditCard | undefined>(
    inputs.payment ? inputs.payment.creditCard : undefined,
  );
  // selectionは選択されたカード、subscription, ticket_bookの情報が保持されている
  // これは支払い方法の選択を変更しても保持される
  const [cardSelection, setCardSelection] = useState<CardSelection | undefined>(
    inputs.payment ? inputs.payment.cardSelection : undefined,
  );
  const [subscriptionSelection, setSubscriptionSelection] = useState<
    string | undefined
  >(undefined);
  const [ticketSelection, setTicketSelection] = useState<string | undefined>(
    undefined,
  );

  const submittable = useMemo(() => {
    return createSubmittable(loginUser, {
      paymentMethod,
      creditCard,
      cardSelection,
      subscriptionSelection,
      ticketSelection,
    });
  }, [
    loginUser,
    paymentMethod,
    creditCard,
    cardSelection,
    subscriptionSelection,
    ticketSelection,
  ]);

  const handleSelectCard = useCallback((cardSelection: CardSelection) => {
    setCardSelection(cardSelection);
  }, []);

  const handleSelectSubscription = useCallback(
    (subscriptionSelection: string) => {
      setSubscriptionSelection(subscriptionSelection);
    },
    [],
  );

  const handleSelectTicket = useCallback((ticketSelection: string) => {
    setTicketSelection(ticketSelection);
  }, []);

  const handleChange = useCallback((creditCard: CreditCard) => {
    setCreditCard(creditCard);
  }, []);

  const handleSelectPaymentMethod = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const paymentMethod = paymentMethods.find(
        (p) => p.method === e.currentTarget.value,
      );
      setCardSelection(
        loginUser.creditCard ? cardSelection || CardSelection.Used : undefined,
      );
      setPaymentMethod(paymentMethod);
    },
    [loginUser, cardSelection, paymentMethods],
  );

  const handleSubmit = useCallback(() => {
    onSubmit({
      cardSelection,
      subscriptionSelection,
      ticketSelection,
      creditCard,
      firstTimeFreeCouponPublicId: firstTimeFreeCoupon?.publicId,
      paymentMethod: paymentMethod!,
      email: userEmail,
    });
  }, [
    cardSelection,
    subscriptionSelection,
    ticketSelection,
    creditCard,
    paymentMethod,
    firstTimeFreeCoupon?.publicId,
    onSubmit,
    userEmail,
  ]);

  const paymentMethodsExceptCoupon = paymentMethods.filter(
    (method) => method.method !== 'first_time_free_voucher',
  );

  const paymentMethodsExceptCouponElement = useCallback(() => {
    return (
      <>
        <PaymentMethodSelect
          paymentMethods={paymentMethodsExceptCoupon}
          paymentMethod={paymentMethod}
          onChangePaymentMethod={handleSelectPaymentMethod}
          onSelectCreditCard={handleSelectCard}
          onSelectSubscription={handleSelectSubscription}
          onSelectTicket={handleSelectTicket}
          isCreditCardShown={showableInputMyCreditCard(
            loginUser,
            paymentMethod,
          )}
          isSubscriptionShown={showableInputMySubscription(paymentMethod)}
          isTicketBookShown={showableInputMyTicket(paymentMethod)}
          cardSelection={cardSelection}
          ticketSelection={ticketSelection}
          subscriptionSelection={subscriptionSelection}
          loginUser={loginUser}
        />
        {showableInputCreditCard(loginUser, paymentMethod, cardSelection) && (
          <InputPaymentCreditCard
            paymentMethod={paymentMethod}
            error={errors ? errors.creditCard : undefined}
            creditCard={creditCard || {}}
            onChange={handleChange}
          />
        )}
      </>
    );
  }, [
    cardSelection,
    paymentMethodsExceptCoupon,
    paymentMethod,
    handleSelectPaymentMethod,
    handleSelectSubscription,
    loginUser,
    creditCard,
    errors,
    handleChange,
    handleSelectCard,
    handleSelectTicket,
    subscriptionSelection,
    ticketSelection,
  ]);

  return (
    <>
      <div className={classNames(css.label, css.pl_18px)}>
        {intl.formatMessage({
          id: 'books.formsLabel.pleaseSelectPaymentMethod',
        })}
      </div>
      {isAvailabelFirstTimeFreeCoupon && firstTimeFreeCoupon && (
        <PaymentMethodsWithCoupon
          isAvailableFirstTimeVoucher={isAvailabelFirstTimeFreeCoupon}
          paymentElementsExceptCoupon={paymentMethodsExceptCouponElement()}
          firstTimeFreeCoupon={firstTimeFreeCoupon}
          onSelectFirstTimeFreeCoupon={() =>
            setPaymentMethod(firstTimeFreeCoupon?.paymentMethod)
          }
          onSelectNotCouponPayment={() => {
            setPaymentMethod(undefined);
          }}
        />
      )}
      {!isAvailabelFirstTimeFreeCoupon && paymentMethodsExceptCouponElement()}
      {error && (
        <div className={classNames(css.mt_16px, css.pl_18px)}>
          <PaymentMethodErrorMessage errorType={error} />
        </div>
      )}
      <div className="px-[18px] py-5">
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={!submittable || !postable}
          type="button"
          value={intl.formatMessage({
            id: 'books.submitLabel.confirm',
          })}
          onClick={handleSubmit}
        >
          {intl.formatMessage({
            id: 'books.submitLabel.confirm',
          })}
        </RSButton>
      </div>
    </>
  );
};

export default InputPayment;
